/**
 * Payment processing service for Google Pay
 */

import calculateHash from '../../../../../services/fingerprintjs2';

export class GooglePayPaymentService {
  constructor() {
    this.baseUrl = '/pay-by-wallet';
  }

  /**
   * Create payment intent on server
   * @param {Object} formData - form data from Vue component
   * @param {Object} vueComponent - Vue component instance for accessing ipqualityscore_calculate
   * @returns {Promise<Object>} - payment intent response
   */
  async createPaymentIntent(formData, vueComponent) {
    try {
      // get real fingerprint
      const fingerprint = await calculateHash();
      
      // get IPQS data using the proper checkout method
      let ipqsData = {};
      try {
        let cachedIpqs = localStorage.getItem('3ds_ipqs');
        if (!cachedIpqs) {
          // use the proper ipqualityscore_calculate method from checkout component
          const result = await vueComponent.ipqualityscore_calculate();
          cachedIpqs = result ? JSON.stringify(result) : null;
        }
        if (cachedIpqs) {
          ipqsData = JSON.parse(cachedIpqs);
        }
      } catch (error) {
        ipqsData = {};
      }
      
      const data = {
        product: {
          qty: formData.deal,
          sku: formData.variant,
          is_warranty_checked: formData.warranty
        },
        method: 'google_pay',
        ipqs: ipqsData,
        f: fingerprint
      };

      // add all js_query_params to the request (like in pay-by-card)
      for (let key of Object.keys(js_query_params)) {
        data[key] = js_query_params[key];
      }

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.head.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(data)
      });

      return await response.json();
    } catch (error) {
      console.error('Failed to create payment intent:', error);
      throw error;
    }
  }

  /**
   * Handle successful payment completion
   * @param {Object} paymentIntent - payment intent response
   * @param {Function} setDataToLocalStorage - Vue component method
   * @param {Function} gotoUpsells - Vue component method
   */
  handlePaymentSuccess(paymentIntent, setDataToLocalStorage, gotoUpsells) {
    // store order info in localStorage (same as other payment methods)
    if (paymentIntent.order_id) {
      localStorage.setItem('odin_order_id', paymentIntent.order_id);
    }
    if (paymentIntent.order_currency) {
      localStorage.setItem('order_currency', paymentIntent.order_currency);
    }
    if (paymentIntent.order_number) {
      localStorage.setItem('order_number', paymentIntent.order_number);
    }
    if (paymentIntent.id) {
      localStorage.setItem('order_id', paymentIntent.id);
    }

    // save payment provider as 'stripe' so upsells page shows Google Pay button
    setDataToLocalStorage({
      paymentProvider: 'stripe'  // Google Pay uses Stripe provider
    });

    // set order creation time to prevent duplicate orders (like other payment methods)
    localStorage.setItem('odin_order_created_at', new Date());

    // redirect to thank you page using order_id (like PayPal does)
    gotoUpsells(paymentIntent.order_id, paymentIntent.order_currency);
  }

  /**
   * Calculate order amount based on form data (GPay, no installments)
   * @param {Object} formData - form data from Vue component
   * @param {Object} productPrices - product prices from js_data
   * @returns {number} - amount for Stripe (with correct multiplier)
   */
  calculateOrderAmount(formData, productPrices) {
    if (!formData.deal) return 0;

    const deal = formData.deal;
    const isWarrantyChecked = formData.warranty;
    const prices = productPrices[deal];

    if (!prices) return 0;

    // GPay: ignore installments, use base price
    const amount = isWarrantyChecked ? prices.total_amount : prices.value;

    // Determine currency (from prices or js_data)
    let currency = prices.currency;
    if (!currency && typeof js_data !== "undefined" && js_data.product && js_data.product.prices) {
      currency = js_data.product.prices.currency;
    }
    currency = (currency || '').toUpperCase();

    // Multiplier logic (same as StripeAmountMapper and StripePayment.vue)
    const noMult = [
      "BIF", "CLP", "DJF", "GNF", "JPY", "KMF", "KRW", "MGA", "PYG",
      "RWF", "UGX", "VND", "VUV", "XAF", "XOF", "XPF"
    ];
    const multiplier = noMult.indexOf(currency) === -1 ? 100 : 1;

    return Math.round(amount * multiplier);
  }
}

export default GooglePayPaymentService;